import axios, { type CancelToken } from 'axios'
import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import { type DoctorStationException } from '@/Network/Types/Responses/DoctorStationException'
import {
  CreateExceptionPayload,
  type ExceptionSummary,
  type ExceptionStatusSummaryResponse,
  ListExceptionResponse,
  ListSingleExceptionResponse,
  type ReversalResultDto,
  type SimulateExceptionPayload,
  type SimulateExceptionResponse
} from '../Types/Requests/DoctorStationExceptionRequests'
import type { ValidationRole } from '@/types/approval-flow.types'

interface ListAllExceptionsQuery {
  limit?: number
  offset?: number
  search?: string
  status?: string
  scheduleIds?: string
  healthUnitIds?: string
  startDate?: string
  endDate?: string
  minValue?: number
  maxValue?: number
}

export default class ExceptionService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/doctor-station-exceptions'
  }

  async list(params?: ListAllExceptionsQuery): Promise<ListSingleExceptionResponse> {
    try {
      const { data } = await request.get<ListSingleExceptionResponse>(`${this.getCompletePath()}`, {
        ...params
      })

      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os pedidos de excepcionalidade')
    }
  }

  async getStatusSummary(params?: Record<string, string>): Promise<ExceptionStatusSummaryResponse> {
    try {
      const { data } = await request.get<ExceptionStatusSummaryResponse>(
        this.getCompletePath('/summary'),
        { ...params }
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar o resumo de excepcionalidades')
    }
  }

  async createForDoctorStation(
    doctorStationId: number,
    payload: CreateExceptionPayload
  ): Promise<DoctorStationException> {
    try {
      const path = this.getCompletePath(`/doctor-station/${doctorStationId}`)
      const { data } = await request.post<DoctorStationException>(path, payload)
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível criar a solicitação de excepcionalidade')
    }
  }

  async getByDoctorStationId(doctorStationId: number): Promise<ListExceptionResponse> {
    try {
      const path = this.getCompletePath(`/doctor-station/${doctorStationId}`)
      const { data } = await request.get<ListExceptionResponse>(path)

      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as excepcionalidades do plantão')
    }
  }

  async show(id: number): Promise<ListExceptionResponse> {
    try {
      const { data } = await request.get<ListExceptionResponse>(`${this.getCompletePath()}/${id}`)

      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar o pedido de excepcionalidade')
    }
  }

  async getDetails(exceptionId: number, scheduleId?: number): Promise<ExceptionSummary> {
    try {
      const path = this.getCompletePath(`/${exceptionId}`)
      const params = scheduleId ? { scheduleId } : undefined
      const { data } = await request.get<ExceptionSummary>(path, params)
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os detalhes da excepcionalidade')
    }
  }

  async approve(
    exceptionId: number,
    justification?: string,
    scheduleId?: number,
    approverRole?: ValidationRole
  ): Promise<DoctorStationException> {
    try {
      const path = this.getCompletePath(`/${exceptionId}/approve`)
      const payload = { justification, scheduleId, approverRole }
      const { data } = await request.patch(path, payload)
      return data as DoctorStationException
    } catch (e) {
      throw this.handleError(e, 'Não foi possível aprovar a solicitação de excepcionalidade')
    }
  }

  async reject(
    exceptionId: number,
    justification: string,
    scheduleId?: number,
    approverRole?: ValidationRole
  ): Promise<DoctorStationException> {
    try {
      const path = this.getCompletePath(`/${exceptionId}/reject`)
      const payload = { justification, scheduleId, approverRole }
      const { data } = await request.patch(path, payload)
      return data as DoctorStationException
    } catch (e) {
      throw this.handleError(e, 'Não foi possível rejeitar a solicitação de excepcionalidade')
    }
  }

  async cancel(exceptionId: number, scheduleId: number): Promise<DoctorStationException> {
    try {
      const path = this.getCompletePath(`/${exceptionId}/cancel/${scheduleId}`)
      const { data } = await request.patch(path, {})
      return data as DoctorStationException
    } catch (e) {
      throw this.handleError(e, 'Não foi possível cancelar a solicitação de excepcionalidade')
    }
  }

  /**
   * Inicia a reversão de uma excepcionalidade aprovada. Na reversão multi-etapa
   * o clique já conta como voto no `approverRole`; na imediata (backend antigo,
   * flag desligada ou escala sem fluxo) o papel é ignorado.
   */
  async revert(
    exceptionId: number,
    justification: string,
    scheduleId: number,
    approverRole?: ValidationRole
  ): Promise<ReversalResultDto> {
    try {
      const path = this.getCompletePath(`/${exceptionId}/revert`)
      // `approverRole` só entra no corpo quando informado: o backend antigo não
      // conhece o campo.
      const payload = approverRole
        ? { justification, scheduleId, approverRole }
        : { justification, scheduleId }
      const { data } = await request.patch<ReversalResultDto>(path, payload)

      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível reverter a solicitação de excepcionalidade')
    }
  }

  async approveReversal(
    exceptionId: number,
    scheduleId: number,
    justification?: string,
    approverRole?: ValidationRole
  ): Promise<ReversalResultDto> {
    try {
      const path = this.getCompletePath(`/${exceptionId}/reversal/approve`)
      const payload = { justification, scheduleId, approverRole }
      const { data } = await request.patch<ReversalResultDto>(path, payload)
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível aprovar a reversão da excepcionalidade')
    }
  }

  async rejectReversal(
    exceptionId: number,
    scheduleId: number,
    justification: string,
    approverRole?: ValidationRole
  ): Promise<ReversalResultDto> {
    try {
      const path = this.getCompletePath(`/${exceptionId}/reversal/reject`)
      const payload = { justification, scheduleId, approverRole }
      const { data } = await request.patch<ReversalResultDto>(path, payload)
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível recusar a reversão da excepcionalidade')
    }
  }

  async simulate(
    doctorStationId: number,
    payload: SimulateExceptionPayload,
    cancelToken?: CancelToken
  ): Promise<SimulateExceptionResponse> {
    try {
      const path = this.getCompletePath(`/${doctorStationId}/simulate`)
      const { data } = await request.post<SimulateExceptionResponse>(path, payload, {
        cancelToken
      })
      return data
    } catch (e) {
      // Cancelamento (debounce/nova simulação) segue cru: o consumidor
      // (useExceptionSimulation) o detecta via `axios.isCancel` — embrulhar em
      // ServiceError transformaria cancelamento em erro visível.
      if (axios.isCancel(e)) throw e
      throw this.handleError(e, 'Não foi possível calcular os valores da excepcionalidade')
    }
  }
}
